import React from 'react';
import { X, ArrowUpRight } from 'lucide-react';
import { Project } from '../data/portfolio-data';
import { ResponsiveContainer, LineChart, BarChart, CartesianGrid, XAxis, YAxis, Tooltip, Legend, Line, Bar } from 'recharts';

interface ProjectDetailsModalProps {
  project: Project;
  onClose: () => void;
}

const ProjectDetailsModal: React.FC<ProjectDetailsModalProps> = ({ project, onClose }) => {
  const hasAutomationData = project.performanceData[0]?.automationLevel !== undefined;
  const hasTrafficData = project.performanceData[0]?.sessions2024 !== undefined;

  const closeAndScroll = () => {
    onClose();
    const sec = document.getElementById('contact');
    if (sec) sec.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="fixed inset-0 bg-blue-900 bg-opacity-90 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-blue-800 rounded-xl overflow-y-auto max-h-full w-full max-w-4xl transform transition-all duration-300 animate-fade-in-up">
        <div className="relative bg-gradient-to-r from-blue-900 to-blue-800 p-8 rounded-t-xl">
          <button onClick={onClose} className="absolute top-4 right-4 p-2 text-blue-200 hover:text-white">
            <X size={24} />
          </button>
          <h2 className="text-4xl font-bold text-white mb-2">{project.title}</h2>
          <p className="text-blue-200">{project.clientIndustry} · {project.year}</p>
        </div>
        <div className="p-8 text-blue-100 space-y-8">
          <section>
            <h3 className="text-2xl font-semibold text-white mb-2">Challenge</h3>
            <p className="whitespace-pre-line">{project.challenge}</p>
          </section>
          <section>
            <h3 className="text-2xl font-semibold text-white mb-2">Solution</h3>
            <p className="whitespace-pre-line">{project.solution}</p>
          </section>
          <section>
            <h3 className="text-2xl font-semibold text-white mb-2">Results</h3>
            <p className="whitespace-pre-line">{project.results}</p>
          </section>
          {project.performanceMetrics && (
            <section>
              <h3 className="text-2xl font-semibold text-white mb-4">Key Metrics Comparison</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {Object.entries(project.performanceMetrics.beforeOptimization).map(([key, beforeVal]) => {
                  const afterVal = project.performanceMetrics?.afterOptimization[key as keyof typeof project.performanceMetrics.afterOptimization];
                  if (!beforeVal || !afterVal) return null;
                  const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
                  const improvement = key.includes('Time') || key.includes('Rate') 
                    ? ((Number(beforeVal.replace(/[^0-9.]/g, '')) - Number(afterVal.replace(/[^0-9.]/g, ''))) / Number(beforeVal.replace(/[^0-9.]/g, '')) * 100).toFixed(0)
                    : ((Number(afterVal.replace(/[^0-9.]/g, '')) - Number(beforeVal.replace(/[^0-9.]/g, ''))) / Number(beforeVal.replace(/[^0-9.]/g, '')) * 100).toFixed(0);
                  
                  return (
                    <div key={key} className="bg-blue-900/30 rounded-lg p-6 transform transition-all duration-300 hover:scale-105">
                      <p className="text-blue-300 text-sm uppercase mb-2">{label}</p>
                      <div className="space-y-2">
                        <div className="flex items-baseline justify-between">
                          <p className="text-sm text-blue-400">Before</p>
                          <p className="text-lg text-red-400 line-through">{beforeVal}</p>
                        </div>
                        <div className="flex items-baseline justify-between">
                          <p className="text-sm text-blue-400">After</p>
                          <p className="text-2xl font-bold text-green-400">{afterVal}</p>
                        </div>
                        <div className="pt-2 border-t border-blue-700">
                          <p className="text-right text-blue-300">
                            {improvement}% {key.includes('Time') || key.includes('Rate') ? 'Reduction' : 'Improvement'}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}
          {project.keyAchievements && project.keyAchievements.length > 0 && (
            <section>
              <h3 className="text-2xl font-semibold text-white mb-4">Achievements</h3>
              <ul className="list-disc list-inside space-y-2">
                {project.keyAchievements.map((ach, idx) => (
                  <li key={idx} className="text-blue-100">{ach}</li>
                ))}
              </ul>
            </section>
          )}
          {hasAutomationData && (
            <section>
              <h3 className="text-2xl font-semibold text-white mb-4">System Automation Impact</h3>
              <div className="h-64 mb-8">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={project.performanceData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e3a8a" />
                    <XAxis dataKey="month" stroke="#93c5fd" />
                    <YAxis stroke="#93c5fd" />
                    <Tooltip contentStyle={{ backgroundColor: '#1e3a8a', border: 'none' }} itemStyle={{ color: '#93c5fd' }} />
                    <Line type="monotone" dataKey="automationLevel" name="Automation Level" stroke="#3b82f6" strokeWidth={2} />
                    <Line type="monotone" dataKey="userConfidence" name="User Confidence" stroke="#60a5fa" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <h3 className="text-2xl font-semibold text-white mb-4">Recovery Time Improvement</h3>
              <div className="h-64 mb-8">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={project.performanceData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e3a8a" />
                    <XAxis dataKey="month" stroke="#93c5fd" />
                    <YAxis stroke="#93c5fd" />
                    <Tooltip contentStyle={{ backgroundColor: '#1e3a8a', border: 'none' }} itemStyle={{ color: '#93c5fd' }} />
                    <Bar dataKey="Manual" name="Manual Recovery (min)" fill="#ef4444" />
                    <Bar dataKey="Automated" name="Automated Recovery (min)" fill="#22c55e" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </section>
          )}
          {hasTrafficData && (
            <section>
              <h3 className="text-2xl font-semibold text-white mb-4">Traffic Improvements</h3>
              <div className="h-64 mb-8">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={project.performanceData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e3a8a" />
                    <XAxis dataKey="month" stroke="#93c5fd" />
                    <YAxis stroke="#93c5fd" />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="sessions2024" name="Sessions 2024" fill="#22c55e" />
                    <Bar dataKey="sessions2023" name="Sessions 2023" fill="#ef4444" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </section>
          )}
          <section>
            <h3 className="text-2xl font-semibold text-white mb-4">Technologies Used</h3>
            <div className="flex flex-wrap gap-4">
              {project.techStack.map((tech) => (
                <span key={tech} className="px-4 py-2 bg-blue-900 bg-opacity-50 rounded-full text-blue-200 text-sm">
                  {tech}
                </span>
              ))}
            </div>
          </section>
          <div className="text-center">
            <button onClick={closeAndScroll} className="mt-8 px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-semibold inline-flex items-center">
              Start Your Project With Me <ArrowUpRight className="ml-2" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetailsModal; 